import type { PipelineFehlerKategorie } from './test-run.model';
import { CoverageKind, TestCasePriority } from './test-case.model';

export type KiSperrgrund =
  | 'firma'
  | 'gesperrt'
  | 'projekt'
  | 'verbindung'
  | 'funktion-nicht-erlaubt'
  | 'funktion-aus'
  | 'kontingent'
  | 'rechte'
  | 'ungespeichert'
  | 'titel-fehlt'
  | 'keine-schritte'
  | 'zu-viele-schritte'
  | 'zu-lang'
  | 'status-fehler';

/** i18n-Schlüssel des Sperrtexts je Grund (Texte aus page-ki.jsx:26-37 und Klärung 02.10.2026). */
export const KI_SPERRTEXT_KEYS: Record<KiSperrgrund, string> = {
  firma: 'ki.sperrgrund.firma',
  gesperrt: 'ki.sperrgrund.gesperrt',
  projekt: 'ki.sperrgrund.projekt',
  verbindung: 'ki.sperrgrund.verbindung',
  'funktion-nicht-erlaubt': 'ki.sperrgrund.funktionNichtErlaubt',
  'funktion-aus': 'ki.sperrgrund.funktionAus',
  kontingent: 'ki.sperrgrund.kontingent',
  rechte: 'ki.sperrgrund.rechte',
  ungespeichert: 'ki.sperrgrund.ungespeichert',
  // Nicht im Design: Anforderung beim Anlegen ohne Titel (NEX-2339 AK2).
  'titel-fehlt': 'ki.anforderungTools.titelFehlt',
  // Nicht im Design: die Grenzen des Qualitätschecks (mindestens ein Schritt, Grenzen des Backends).
  'keine-schritte': 'ki.qualitaet.keineSchritte',
  'zu-viele-schritte': 'ki.qualitaet.zuVieleSchritte',
  'zu-lang': 'ki.qualitaet.zuLang',
  // Eigener Schlüssel statt ki.sperrgrund.*: dort prüft die Sperrtext-Erwartung die Designtexte 1:1 und bleibt so unverändert.
  'status-fehler': 'ki.status.ladefehler',
};

export type KiButtonState = 'aktiv' | 'laeuft' | 'gesperrt';

/** Nur die Größen, die das Design verwendet (page-ki.jsx und Aufrufer). */
export type KiMarkSize = 13 | 14 | 15 | 16 | 18 | 22 | 24 | 26 | 28 | 30 | 36;

export type KiFunktion = 'TESTDESIGN' | 'PIPELINE_ANALYSE';

/** Gründe, die `GET …/ki/status` liefern kann; die übrigen entstehen nur im Frontend. */
export type KiStatusGrund = Exclude<
  KiSperrgrund,
  'ungespeichert' | 'titel-fehlt' | 'keine-schritte' | 'zu-viele-schritte' | 'zu-lang' | 'status-fehler'
>;

export interface KiFunktionStatus {
  aktiv: boolean;
  grund?: KiStatusGrund;
  text?: string;
}

export type KiRegion = 'EU' | 'DRITTLAND' | 'LOKAL';

/** Bekannte Regionen übersetzt das Frontend; eine unbekannte ID bleibt unverändert sichtbar. */
export const KI_REGION_KEYS: Record<KiRegion, string> = {
  EU: 'ki.region.EU',
  DRITTLAND: 'ki.region.DRITTLAND',
  LOKAL: 'ki.region.LOKAL',
};

export interface KiVerbindungInfo {
  anbieter: string;
  modell: string;
  region: KiRegion;
}

/** Antwort von `GET /api/v1/projects/{projectId}/ki/status`. */
export interface KiStatus {
  funktionen: Partial<Record<KiFunktion, KiFunktionStatus>>;
  verbindung?: KiVerbindungInfo;
}

export interface KiStatusLaedt {
  art: 'laedt';
}

export interface KiStatusFehler {
  art: 'fehler';
}

export interface KiStatusGeladen {
  art: 'geladen';
  status: KiStatus;
}

export type KiStatusZustand = KiStatusLaedt | KiStatusFehler | KiStatusGeladen;

export type KiRegionenFreigabe = 'NUR_EU' | 'EU_UND_DRITTLAENDER' | 'NUR_LOKAL';

export type KiAnbieterTyp = 'OPENAI' | 'AZURE_OPENAI' | 'MISTRAL' | 'OLLAMA' | 'EIGENER_ENDPUNKT';

export interface KiAnbieterEinstellung {
  id: KiAnbieterTyp;
  erlaubt: boolean;
  endpunkt: string | null;
}

export interface KiFunktionen {
  testdesign: boolean;
  pipelineAnalyse: boolean;
  mcp: boolean;
}

/** Body von GET/PUT /api/v1/ki/einstellungen (KiMandantEinstellungenDTO im Backend). */
export interface KiMandantEinstellungen {
  aktiv: boolean;
  regionen: KiRegionenFreigabe;
  anbieter: KiAnbieterEinstellung[];
  funktionen: KiFunktionen;
  eigeneMuster: string[];
  kontingentTokensProMonat: number | null;
  maxAufbewahrungTage: 30 | 60 | 90 | 180;
  auditInhalte: boolean;
  /** Nur in der Antwort: gespeicherte Muster, die die heutige Prüfung nicht mehr bestehen. */
  musterHinweise?: KiMusterHinweis[];
}

export interface KiMusterHinweis {
  index: number;
  grund: string;
}

export const KI_STATUS_GRUENDE: readonly KiStatusGrund[] = [
  'firma',
  'gesperrt',
  'projekt',
  'verbindung',
  'funktion-nicht-erlaubt',
  'funktion-aus',
  'kontingent',
  'rechte',
];

/** Pfad-Segment von `POST …/ki/{art}` (klein geschrieben laut Schnittstelle). */
export type KiAnfragePfad = 'testfall-entwuerfe' | 'abdeckung' | 'qualitaetscheck' | 'pipeline-analyse';

/** `art` in der Antwort von `GET …/ki/anfragen/{anfrageId}`: Enum-Name von KiAnfrageArt im Backend. */
export type KiAnfrageArt = 'TESTFALL_ENTWUERFE' | 'ABDECKUNG' | 'QUALITAETSCHECK' | 'PIPELINE_ANALYSE';

export const KI_ANFRAGE_FUNKTION: Record<KiAnfragePfad, KiFunktion> = {
  'testfall-entwuerfe': 'TESTDESIGN',
  abdeckung: 'TESTDESIGN',
  qualitaetscheck: 'TESTDESIGN',
  'pipeline-analyse': 'PIPELINE_ANALYSE',
};

/** Eigener Start-Endpunkt einer Funktion; `pfad` ist relativ zu `/api/v1/projects/{projectId}/`. */
export interface KiStartEndpunkt {
  pfad: string;
  body: object;
}

/** Was eine KI-Funktion anfragt: `POST …/ki/{art}` mit dem Kontext im Body, oder ihr eigener `start`. */
export interface KiAuftrag {
  projectId: string;
  art: KiAnfragePfad;
  kontextTyp: 'REQUIREMENT' | 'TESTCASE' | 'TEST_RUN';
  /** Null bei einer Anforderung vor dem ersten Speichern; dann trägt `start` den Inhalt. */
  kontextId: string | null;
  start?: KiStartEndpunkt;
}

export type KiAnfrageStatus = 'LAEUFT' | 'FERTIG' | 'FEHLER';

/** Was an den Anbieter ging; Anzeige in KiGesendetComponent. */
export interface KiGesendet {
  anbieter: string;
  modell: string;
  region: string;
  inhalte: string[];
  eigeneMuster: number;
}

/** Antwort von `GET …/ki/anfragen/{anfrageId}`; `ergebnis` hat je Art eine eigene Form. */
export interface KiAnfrage {
  id: string;
  art: KiAnfrageArt;
  status: KiAnfrageStatus;
  kontextTyp: KiAuftrag['kontextTyp'];
  kontextId: string | null;
  fehlercode?: string;
  grund?: string;
  fehlertext?: string;
  gesendet: KiGesendet & { texte: string[] };
  ergebnis?: unknown;
  ergebnisGeloescht: boolean;
  erstelltAm: string;
  abgeschlossenAm?: string;
}

export type KiPipelineSicherheit = 'hoch' | 'mittel' | 'niedrig';

/** Empfohlene nächste Aktion: Build-Fehler gehen an die Entwicklung, alles andere wird ein Bug. */
export type KiPipelineAktionTyp = 'ENTWICKLUNG' | 'BUG';

/** Die empfohlene Aktion samt dem Text der Analyse, mit dem die Seite den Bug-Dialog vorbelegt. */
export interface KiPipelineAktionVorlage {
  aktionTyp: KiPipelineAktionTyp;
  kategorie: PipelineFehlerKategorie;
  zusammenfassung: string;
  ursache: string;
}

export interface KiPipelineLogzeile {
  nr: number;
  text: string;
  /** Zeile kam im letzten erfolgreichen Lauf nicht vor. */
  neu: boolean;
}

export interface KiPipelineLogstelle {
  job: string;
  /** null, wenn der Lauf den fehlgeschlagenen Schritt nicht kennt. */
  schritt: string | null;
  von: number;
  bis: number;
  url?: string | null;
  zeilen: KiPipelineLogzeile[];
}

/** `ergebnis` einer Anfrage `PIPELINE_ANALYSE` (KiPipelineErgebnis im Backend). */
export interface KiPipelineErgebnis {
  zusammenfassung: string;
  ursache: string;
  /** Fehlt, wenn die KI keinen bekannten Wert liefert. */
  sicherheit?: KiPipelineSicherheit;
  /** Kategorie, Aktion und Testzahlen fehlen, wenn die Ausführung nicht mehr existiert. */
  kategorie?: string;
  aktionTyp?: KiPipelineAktionTyp;
  tests?: { fehlgeschlagen: number; gesamt: number };
  analysiertAm: string;
  logstellen: KiPipelineLogstelle[];
  /** Aufbewahrungsfrist der Logausschnitte im Projekt. */
  aufbewahrungTage?: number;
  /** Gleiche Kategorie in den letzten `anzahl` Ausführungen; `commit` der ersten davon (NEX-1551c). */
  wiederkehrend?: KiPipelineWiederkehrend;
}

export interface KiPipelineWiederkehrend {
  anzahl: number;
  commit?: string | null;
}

export type KiAbdeckungZustand = 'ABGEDECKT' | 'NICHT_ABGEDECKT' | 'NICHT_VERKNUEPFT';

/** Ein Aspekt im `ergebnis` einer Anfrage der Art ABDECKUNG (NEX-1542). */
export interface KiAbdeckungAspekt {
  titel: string;
  abgedeckt: boolean;
  /** Fehlt in älteren gespeicherten Ergebnissen; dann gilt `abgedeckt`. */
  zustand?: KiAbdeckungZustand;
  testCaseId?: string;
  testCaseKey?: string;
  begruendung: string;
  /** Null ohne Bezug auf ein Akzeptanzkriterium (Anforderungstitel); fehlt in älteren gespeicherten Ergebnissen. */
  kriteriumId?: string | null;
}

/** Lücke aus „Abdeckung prüfen“, für die der Dialog „Testfälle vorschlagen“ geöffnet wird. */
export interface KiAbdeckungLuecke {
  titel: string;
  kriteriumId: string | null;
}

export interface KiAbdeckungErgebnis {
  aspekte: KiAbdeckungAspekt[];
}

export type KiRunZustand =
  | { art: 'bereit' }
  | { art: 'laeuft' }
  | { art: 'fertig'; anfrage: KiAnfrage }
  | { art: 'fehler'; textKey: string };

/** Antwort von `GET /api/v1/users/me/ki-mcp`: MCP-Block im Konto zeigen (Mandant KI an und MCP erlaubt). */
export interface KiMcpSichtbarkeit {
  sichtbar: boolean;
}

export type KiEntwurfArt = 'POSITIV' | 'NEGATIV' | 'GRENZWERT';

const KI_DECKUNGSART: Record<KiEntwurfArt, CoverageKind> = {
  POSITIV: 'POSITIVE',
  NEGATIV: 'NEGATIVE',
  GRENZWERT: 'EDGE_CASE',
};

/** Deckungsart, die das Backend der Verknüpfung bei der Übernahme gibt; ohne oder mit unbekannter Art gilt Positiv. */
export function kiDeckungsart(art: KiEntwurfArt | null): CoverageKind {
  return KI_DECKUNGSART[art ?? 'POSITIV'] ?? 'POSITIVE';
}

export interface KiEntwurfSchritt {
  nr: number;
  schritt: string;
  erwartung: string;
}

/** Ein Entwurf aus `ergebnis.entwuerfe` einer Anfrage TESTFALL_ENTWUERFE; die KI kann Textfelder auslassen. */
export interface KiTestfallEntwurf {
  entwurfId: string;
  titel: string | null;
  art: KiEntwurfArt | null;
  prioritaet: TestCasePriority | null;
  vorbedingung: string | null;
  schritte: KiEntwurfSchritt[];
}

/** `ergebnis` einer fertigen Anfrage TESTFALL_ENTWUERFE: angezeigte Entwürfe und Anzahl der ausgelassenen. */
export interface KiTestfallEntwuerfeErgebnis {
  entwuerfe: KiTestfallEntwurf[];
  ausgelassen: number;
}

/**
 * Ungespeicherter Inhalt einer Anforderung beim Anlegen, Body von `POST …/requirements/ki/testfall-vorschlaege` und
 * `…/requirements/ki/abdeckung`; Kriterien in Formular-Reihenfolge, `testCaseIds` sind die im Formular verknüpften.
 */
export interface KiAnforderungInhalt {
  titel: string;
  beschreibung: string | null;
  kriterien: { id: string; text: string }[];
  testCaseIds: string[];
}

/** Übernommener Entwurf einer noch nicht gespeicherten Anforderung, mit dem (geänderten) Titel der Karte. */
export interface KiEntwurfVorgemerkt extends Omit<KiTestfallEntwurf, 'titel'> {
  titel: string;
}

/** Zustand einer Entwurfskarte im Panel; „verworfen“ wird nicht gespeichert. */
export type KiEntwurfStand = 'offen' | 'uebernommen' | 'verworfen';

/** Ergebnis von `POST …/ki/entwuerfe/{entwurfId}/uebernehmen`, Fehler schon in Text übersetzt. */
export type KiEntwurfUebernahme =
  | { art: 'angelegt'; testCaseId: string; key: string }
  | { art: 'bereitsUebernommen' }
  | { art: 'fehler'; textKey: string; erneutMoeglich: boolean };

/** Was die Karte nach einer neuen Anlage meldet, damit das Panel den Testfall in die Verknüpfungsliste nimmt. */
export interface KiEntwurfAngelegt {
  entwurfId: string;
  testCaseId: string;
  key: string;
  titel: string;
}

/** Meldung des Panels an die Anforderung nach einer neuen Anlage: die Verknüpfung und, getrennt davon, was die Zeile zeigt. */
export interface KiTestfallUebernommen {
  link: { testCaseId: string; coverageKind: string };
  testfall: { id: string; key: string; titel: string };
}

export type KiBefundSchwere = 'PROBLEM' | 'VERBESSERUNG' | 'HINWEIS';

export type KiBefundFeld = 'ERWARTUNG' | 'VORBEDINGUNG' | 'SCHRITT';

/** Ein Schritt im Body von `POST …/test-cases/{testCaseId}/ki/qualitaet`; `nr` ist die Position in der Tabelle. */
export interface KiQualitaetSchritt {
  nr: number;
  schritt: string;
  erwartung: string;
}

/** Body von `POST …/test-cases/{testCaseId}/ki/qualitaet`: der aktuelle, auch ungespeicherte Stand. */
export interface KiQualitaetInhalt {
  titel: string;
  vorbedingung: string;
  schritte: KiQualitaetSchritt[];
}

/** Befund im `ergebnis` der Anfrage; `schrittNr` ist null beim Befund zur Vorbedingung. */
export interface KiQualitaetBefund {
  schrittNr: number | null;
  schwere: KiBefundSchwere;
  feld: KiBefundFeld;
  text: string;
  vorschlag?: string | null;
}

/** „Übernehmen“ zur Vorbedingung: der Aufrufer übernimmt den Wert als ungespeicherte Änderung. */
export interface KiQualitaetAenderung {
  feld: 'VORBEDINGUNG';
  wert: string;
}

/**
 * Feld eines Befunds: Schritt (`schrittId`, null wenn nicht sicher zuzuordnen) oder Vorbedingung. Trägt die Marker
 * „KI-Befund“ am Feld Vorbedingung.
 */
export interface KiQualitaetZiel {
  feld: KiBefundFeld;
  schrittId: string | null;
}

/** Inhalt eines Befund-Kastens (`app-ki-befund-inline`); ohne `feld` gibt es keinen Diff und kein „Übernehmen“. */
export interface KiBefundAnzeige {
  schwere: KiBefundSchwere;
  feld: KiBefundFeld | null;
  text: string;
  vorschlag?: string | null;
  /** Schritt, den der Befund nennt, wenn er nicht an dessen Zeile steht. */
  schrittNr?: number | null;
}

/** Offener Befund an der Zeile `schrittId`; mit `key` meldet die Tabelle „Übernehmen“ und „Lassen“ zurück. */
export interface KiSchrittBefund extends KiBefundAnzeige {
  key: number;
  schrittId: string;
  feld: 'ERWARTUNG' | 'SCHRITT' | null;
}
